import React, { useRef, MutableRefObject, useContext, useEffect } from 'react';
import { useMutation, useQuery } from 'react-query';
import { useLocation } from 'react-router';
import { PetInterface } from 'types/pet';
import { updatePetProfile } from 'api/petApi';
import { KibButtonNew } from '@chewy/kib-controls-react';
import { PetAvatar, AvatarTypes, AvatarSizes } from '@chewy/pet-profile-pet-avatar';
import useLocationQuery from 'hooks/useLocationQuery';
import classNames from 'classnames';
import AutoshipChangesCard from 'components/autoship-changes-card/AutoshipChangesCard';
import AnalyticsContext from '../../../context/AnalyticsContext';
import { autoshipDeactivatedPetsEnabledLoadData } from '../../../api/featuresApi';
import {
  ApiPetProfile,
  ApiPetProfileStatusEnum,
  ApiPetProfileStatusReasonEnum,
} from '../../../api/generated-client/PetProfile-Service-A.v1/models';
import log from '../../../util/log';
import FetchError from '../../../types/http';
import { handleAuthError } from '../../../util/auth';
import { AUTOSHIP_DEACTIVATED_PETS_ENABLED, getPetMemorialPath } from '../../../util/config';
import { getApiPetProfileFromInterface } from '../utils/getApiPetProfileFromInterface';
import { getSharedCopy, getSympathyStepCopy } from '../copy';
import ManagePetSupport from '../ManagePetSupport/ManagePetSupport';
import '@chewy/pet-profile-pet-avatar/dist/esm/main.css';
import './manage-pet-sympathy.scss';

export type ManagePetSympathyProps = {
  pet: PetInterface;
  statusChoice: ApiPetProfileStatusEnum;
  reasonChoice: ApiPetProfileStatusReasonEnum;
  headerText: string;
  bodyText1: string;
  bodyText2?: string | null;
  avatarStroke?: AvatarTypes;
  setShowError: React.Dispatch<React.SetStateAction<boolean>>;
  setShowUndoConfirmation: React.Dispatch<React.SetStateAction<boolean>>;
};

export const ManagePetSympathy = ({
  pet,
  statusChoice,
  reasonChoice,
  headerText,
  bodyText1,
  bodyText2,
  avatarStroke,
  setShowError,
  setShowUndoConfirmation,
}: ManagePetSympathyProps) => {
  const { data: featureFlagData } = useQuery(
    autoshipDeactivatedPetsEnabledLoadData.key,
    autoshipDeactivatedPetsEnabledLoadData,
  );

  const isAutoshipDeactivatedPetsEnabled =
    featureFlagData?.[AUTOSHIP_DEACTIVATED_PETS_ENABLED]?.toLowerCase() === 'true';

  const {
    signatureText1,
    signatureText2,
    memorialButtonText,
    contactLinkTextPlain,
  } = getSympathyStepCopy(pet, reasonChoice);

  const { restoreText, contactPrompt } = getSharedCopy(pet);

  const analytics = useContext(AnalyticsContext);

  const location = useLocation();
  const clientErrors: MutableRefObject<number> = useRef(0);
  let query = useLocationQuery();
  const redirectUrl = query.get('redirectUrl');
  const memorialLink = redirectUrl
    ? getPetMemorialPath(pet.id) + `?redirectUrl=${encodeURIComponent(redirectUrl)}`
    : getPetMemorialPath(pet.id);

  const determineRetry = (failureCount: number, error: FetchError): boolean => {
    if (error.status >= 400 && error.status < 500) {
      clientErrors.current = clientErrors.current + 1;
      return 3 - clientErrors.current > 0;
    } else {
      return false;
    }
  };

  useEffect(() => {
    analytics.trackRainbow({
      name: 'pet profile rainbow - viewed',
      category: 'pet-profile-rainbow',
      action: 'impression',
      label: 'pet_profile_rainbow_sympathy_impression',
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updatePet = useMutation(
    (payload: ApiPetProfile) => updatePetProfile(Number(payload.id), payload),
    {
      onSuccess: () => {
        setShowUndoConfirmation(true);
      },
      onError: (error: FetchError) => {
        log.error('Failed to Update Pet Profile:', `Status code: ${error.status}`, error);
        handleAuthError(error.status, location.pathname); // TODO check this
        if (error.status >= 500 || clientErrors.current >= 3) {
          setShowError(true);
        }
      },
      retry: determineRetry,
    },
  );

  const isLoading = updatePet.isLoading;

  const handleUndoClick = () => {
    analytics.trackRainbow({
      name: 'pet profile rainbow - clicked',
      category: 'pet-profile-rainbow',
      action: 'click',
      label: 'pet_profile_rainbow_sympathy_restore',
    });
    updatePet.mutate({
      ...getApiPetProfileFromInterface(pet),
      status: ApiPetProfileStatusEnum.Active,
      statusReason: ApiPetProfileStatusReasonEnum.Other,
    });
  };

  return (
    <div className="manage-pet-sympathy">
      <PetAvatar
        src={pet.avatarUrl}
        size={AvatarSizes.MEDIUM}
        strokeType={avatarStroke ? avatarStroke : AvatarTypes.MemorialOther}
      />
      <div className="manage-pet-sympathy__message">
        <p className="manage-pet-sympathy__header" data-testid="manage-pet-sympathy__header">
          {`${headerText}`}
        </p>
        <div className="manage-pet-sympathy__body" data-testid="manage-pet-sympathy__body">
          <p className="manage-pet-sympathy__body-text">{`${bodyText1}`}</p>
          {bodyText2 && <p className="manage-pet-sympathy__body-text">{`${bodyText2}`}</p>}
        </div>
      </div>
      <p className="manage-pet-sympathy__signature">
        {`${signatureText1}`}
        <br />
        {`${signatureText2}`}
      </p>
      <div data-testid="manage-pet-sympathy__ingress-container">
        {statusChoice === ApiPetProfileStatusEnum.Inactive && (
          <KibButtonNew
            className="manage-pet-sympathy__memorial-ingress"
            data-testid="manage-pet-sympathy__memorial-ingress"
            emphasis={isAutoshipDeactivatedPetsEnabled ? 'primary' : 'tertiary'}
            size="large"
            onClick={() => {
              window.location.href = memorialLink;
              analytics.trackRainbow({
                name: 'pet profile rainbow - clicked',
                category: 'pet-profile-rainbow',
                action: 'click',
                label: 'pet_profile_rainbow_sympathy_memorial',
              });
            }}
          >
            {memorialButtonText}
          </KibButtonNew>
        )}
        <AutoshipChangesCard />
        <ManagePetSupport
          className={classNames(`manage-pet-support__manage-pet-sympathy`, {
            'manage-pet-support__padding': statusChoice === ApiPetProfileStatusEnum.Inactive,
          })}
          restoreText={restoreText}
          supportText={contactPrompt}
          supportLink={contactLinkTextPlain}
          onUndoClick={handleUndoClick}
          onClick={() => {
            analytics.trackRainbow({
              name: 'pet profile rainbow - clicked',
              category: 'pet-profile-rainbow',
              action: 'click',
              label: 'pet_profile_rainbow_sympathy_cs',
            });
          }}
        />
      </div>
    </div>
  );
};
